import _ from "lodash";
import React from "react";
import classNames from "classnames";
import styled from "styled-components";
import DateDisplay from "../../../components/DateDisplay";
import InfinityList from "../../../components/tables/InfinityList"
import WidgetSkeleton from "./WidgetSkeleton";
import WidgetMessage, { BaseWidgetMessages } from "./WidgetMessage";
import Skeleton from "react-loading-skeleton";
import useOpenMasterAccount from "../../../hooks/useOpenMasterAccount";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Spinner } from "../../../components/loaders";
import { ThemedSpan, ThemedIcon } from "../../../components/utilities";
import { usePendingBusinessSheetData } from "../hooks/PendingBusinessSheetsContext";
import { usePendingBusinessSheetGroupingData } from "../hooks/PendingBusinessSheetGroupingContext";
import { Card, Col, OverlayTrigger, Popover, Overlay } from "react-bootstrap";
import { toCommas } from "../../../helpers/numbers";
import { getArbitraryColour } from "../../../helpers/colours";

// registers the ability to use charts here
ChartJS.register(ArcElement, Tooltip, Legend);

const LargePopover = styled(Popover)`
    max-width: 500px;
`;

const CardContainer = styled(Card)`
    height: 300px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 16px;
`;

const CardHeader = styled.div`
    margin-bottom: 1rem;
    display: flex;
    justify-content: space-between;
`;

const CardListContainer = styled.div`
    flex: 1;
    max-height: 230px;
`;

const RowDate = ({ data, format = "DD/MM/YYYY" }) => {
    const { statusDate } = data;

    const icon = React.useMemo(() => {
        const { isAbortedStatus, isPendingStatus, isAwaitApproval, isOverdue, isOnHold, isAdministration, isApprovalReceived } = data;
        if (isAbortedStatus) {
            return { i: "fa-circle-xmark", v: 'danger' };
        }
        if ((isPendingStatus || isAwaitApproval) && isOverdue) {
            return { i: "fa-circle-exclamation", v: 'danger' };
        }
        if (isPendingStatus) {
            return { i: "fa-circle-exclamation", v: 'danger' };
        }
        if (isOnHold) {
            return { i: "fa-circle-xmark", v: 'warning' };
        }
        if (isAdministration || isApprovalReceived) {
            return { i: "fa-check-circle", v: 'success' };
        }
        return null;
    }, [ data ]);

    const variant = React.useMemo(() => {
        const { isPendingStatus, isOverdue, isApprovalReceived } = data;
        if (((isPendingStatus || isApprovalReceived) && isOverdue)) {
            return 'danger';
        }
        return 'primary';
    }, [ data ]);

    return (
        <>
            {icon && (
                <ThemedIcon variant={icon.v} icon={icon.i} className="me-2" size="lg" />
            )}
            <DateDisplay 
                format={format}
                variant={variant}
                className="font-monospace"
            >
                {statusDate}
            </DateDisplay>
        </>
    );
};

const RowLoader = ({ style }) => (
    <div className="p-2" style={style}>
        <Skeleton height={64} />
    </div>
);

const RowTooltip = ({ data, format = "DD/MM/YYYY" }) => (
    <OverlayTrigger
        placement="bottom"
        overlay={<LargePopover>
            <Popover.Header>Further Details</Popover.Header>
            <Popover.Body>
                <div style={{ textAlign: "left" }}>
                    <span className="fw-bold">Adviser: </span><span>{data.adviser}</span>
                    <br />
                    <span className="fw-bold">Status: </span><span>{data.statusText}</span>
                    <br />
                    <span className="fw-bold">Sent Date: </span><DateDisplay format={format} className="font-monospace">{data.sentDate}</DateDisplay>
                </div>
            </Popover.Body>
        </LargePopover>
    }>
        <div className="text-right">
            <RowDate data={data} format={format} />
        </div>
    </OverlayTrigger>
);

const PendingBusinessSheetsCardRow = ({ data, index, style }) => {
    const openMasterAccount = useOpenMasterAccount();

    if (index >= data.length) {
        return <RowLoader style={style} />
    }

    const onClientSelected = (e, masterAccountId) => {
        e.preventDefault();
        openMasterAccount(masterAccountId, { suffix: "summary" });
    }

    const {
        accountName,
        accountReference,
        masterAccountId
    } = data[index];

    return (
        <div className="pb-2" style={style}>
            <div className="card h-100 p-2 d-flex flex-row justify-between align-items-center">
                <div className="flex-fill">
                    <a className="text-reset text-decoration-none" 
                        href={`/client/${masterAccountId}/summary`}
                        onClick={(e) => onClientSelected(e, masterAccountId)}
                    >
                        <ThemedSpan variant="primary">{accountName} </ThemedSpan>
                        <ThemedSpan variant="primary">({accountReference})</ThemedSpan>
                    </a>
                </div>
                <RowTooltip data={data[index]} />
            </div>
        </div>
    );
};

const PensingBusinessSheetsWidgetOverlayChart = ({ groupings = [] }) => {
    const dataset = React.useMemo(() => {
        if (_.isEmpty(groupings)) {
            return null;
        }

        const total = _.sum(groupings.map(el => el.total));
        const dataset = groupings.map(el => {
            let colour = getArbitraryColour(el.username);
            let pct = (el.total / total) * 100;
            return {
                label: `${el.username} (${el.total})`,
                labelStyle: colour,
                total: el.total,
                pct
            };
        });

        return {
            labels: dataset.map(el => el.label),
            datasets: [{
                label: '# of pending business sheets',
                data: dataset.map(el => el.total),
                backgroundColor: dataset.map(el => el.labelStyle),
                borderColor: dataset.map(el => el.labelStyle),
                borderWidth: 1
            }]
        };
    }, [ groupings ]);

    if (!dataset) {
        return <WidgetMessage message="Could not load appointment grouping data." />
    }

    return <Pie data={dataset} />
};

const PendingBusinessSheetsWidgetHeader = () => {
    const ref = React.useRef();
    const [isShown, setIsShown] = React.useState(false);
    const { totalPending } = usePendingBusinessSheetData();
    const { data, error, isLoading, isFetching } = usePendingBusinessSheetGroupingData();
    const isAbleToShowSummary = (data && !error && !isLoading && !isFetching) ? true : false;

    const handleOnShow = () => {
        if (isAbleToShowSummary === false) {
            return;
        }
        setIsShown(() => true);
    };

    const handleOnHide = () => {
        if (isAbleToShowSummary === false) {
            return;
        }
        setIsShown(() => false);
    }

    return (
        <CardHeader>
            <div className="flex-fill">
                <ThemedSpan 
                    ref={ref}
                    variant="primary" 
                    className={classNames("fw-bold me-1", { 'text-decoration-underline': isAbleToShowSummary })}
                    style={{ fontSize: "18px" }}
                    onMouseEnter={handleOnShow} 
                    onMouseLeave={handleOnHide}
                >
                    Advice Pending Approval
                </ThemedSpan>
                <Overlay show={isShown} placement="right" target={ref}>
                    <LargePopover 
                        style={{ width: '400px' }}
                        onMouseEnter={handleOnShow} 
                        onMouseLeave={handleOnHide}
                    >
                        <Popover.Header>Grouped by Adviser</Popover.Header>
                        <Popover.Body className="text-left"> 
                            <PensingBusinessSheetsWidgetOverlayChart groupings={data} />
                        </Popover.Body>
                    </LargePopover>
                </Overlay>
                <ThemedSpan variant="primary" className="fw-bold fs-6">({toCommas(totalPending)})</ThemedSpan>
            </div>
            {(isLoading || isFetching) && (
                <div className="d-flex justify-content-center align-items-center">
                    <Spinner />
                </div>
            )}
        </CardHeader>
    );
};

export const PendingBusinessSheetsWidget = () => {
    const { data, error, isClearing, isLoading, isNextPageLoading, isRowLoaded, loadMore, totalCount } = usePendingBusinessSheetData();

    if (isClearing || isLoading) {
        return <WidgetSkeleton />
    }

    if (error) {
        return <WidgetMessage icon={['fas', 'exclamation']} message={BaseWidgetMessages.Error} />
    }

    if (!data || data.length < 1) {
        return <WidgetMessage message={BaseWidgetMessages.NoResult} />
    }

    return (
        <Col xs={12} sm={12} md={6} lg={6} xl={4}>
            <CardContainer>
                <PendingBusinessSheetsWidgetHeader />
                <CardListContainer>
                    <InfinityList
                        style={{ height: '230px' }}
                        itemData={data}
                        enableScrollTracking={false}
                        row={PendingBusinessSheetsCardRow}
                        error={error}
                        isLoading={isNextPageLoading}
                        isItemLoaded={isRowLoaded}
                        loadMoreItems={loadMore}
                        itemSize={64}
                        itemCount={totalCount}
                    />
                </CardListContainer>
            </CardContainer>
        </Col >
    );
}